import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  CheckCircle2, ChevronRight, ArrowRight, Mountain, Activity,
  AlertTriangle, Workflow, Eye, FileText, Map, BarChart3, Compass,
} from "lucide-react";
import usePageview, { getVisitorSession } from "@/lib/usePageview";
import useNoIndex from "@/lib/useNoIndex";
import BespokePageRibbon from "@/components/BespokePageRibbon";
import BespokeWatermark from "@/components/BespokeWatermark";

// Phase 25.16aq — Dayo bespoke proposal page.
// Phase 25.16as (2026-06-01) — Commercial structure pivoted from
// channel-partner-with-A$3k-cap (tone-deaf for someone in Dayo's
// economic class) to strategic-partner-handshake-first (no contract,
// no commission cap, paper on the back end once the first POC closes).
//
// Trigger: Financial broker Dayo met with Manuel 2026-05-31, has
// exploration-mining-company clients. Raised the "DARA-without-SCADA"
// objection — exploration has no live sensors, only assays / geophysics
// / geochem / satellite / regulator filings. This page answers that
// question completely and frames Dayo as a strategic partner
// (handshake-first, no commission cap, paper on the back end —
// appropriate vocabulary for a financial broker who moves real money).
//
// Strategic frame: the page tells Dayo we took his objection seriously,
// shows him exactly how DARA serves exploration (with a downloadable
// mock heatmap brief), and proposes a 3-stage graduation: Stage 1
// open partnership (no contract / no cap, just bring wins),
// Stage 2 papers what was agreed once the first POC closes
// (cash / equity / observer / revenue share — Dayo's shape),
// Stage 3 names him publicly as Founding Strategic Partner.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Six value-add layers DARA produces for an exploration team.
// Same shape as the PremierCompliance CAPABILITIES array — gives
// Dayo's clients tangible features to react to, not vague promises.
const EXPLORATION_LAYERS = [
  {
    Icon: BarChart3,
    code: "01",
    name: "The Geologist's Morning Brief",
    plain: "A five-minute read every weekday morning. Synthesises overnight assays + competitor JORC announcements + satellite-pass anomalies + permit-milestone alerts + cash-runway-vs-work-programme. The chief geologist walks into the daily meeting knowing exactly what changed in the last 24 hours, with no Excel-stitching by hand.",
    builtIn: "Daily 5-min synthesis · per-project dashboards · audit-logged · regulator-pack-ready · sample PDF available below.",
    honest: "This is the highest-value layer for any exploration team I've spoken to so far. The brief writes itself from data the team is already collecting. We're not creating new work — we're surfacing the signal hiding in the noise they already have.",
  },
  {
    Icon: Map,
    code: "02",
    name: "Synthesised Anomaly Heatmap",
    plain: "Every spatially-located data point on the tenement is combined into a single daily confidence heatmap. The output is 'this 2km x 2km square is where the geological story is intensifying, here's why, here's where to drill next.'",
    builtIn: "Single tenement-wide visualisation · target-rating 0-10 scale · daily auto-regenerate · drill-collar overlay · regulator-grade exportable.",
    honest: "The chief geologist still makes the call. We're not pretending DARA discovers gold. We're just making sure they're seeing all six independent signals at once on a Monday morning instead of stitching them together mentally on coffee three.",
  },
  {
    Icon: Activity,
    code: "03",
    name: "Depth-of-Hole Intelligence",
    plain: "For each active drill hole, a depth log brief writes itself the moment the assays land: where the grade exceeds the cutoff, where alteration intensity peaks, where the spectral signature changes, where down-hole geophysics shows resistivity anomalies. Attached to the hole record, queryable forever, indexed by depth and intercept.",
    builtIn: "Per-hole depth log · automatic interval flagging · cross-hole correlation · multi-element index synthesis · exportable to your existing core-shed tooling.",
    honest: "I've never logged a metre of core in my life. I built this layer around what I think the geologists I've spoken to need. Tell me where I've over-engineered or under-specified.",
  },
  {
    Icon: AlertTriangle,
    code: "04",
    name: "JORC 2012 Continuous-Evidence Layer",
    plain: "JORC requires public disclosure of material exploration information promptly. The threshold for 'material' is judgment-heavy and a wrong call ends careers. DARA continuously assesses every new assay result against the company's existing resource statement and flags anything that crosses materiality. Brief lands in the Company Secretary's inbox, not just the geologist's.",
    builtIn: "Per-assay materiality test · clause 19 prima-facie engine · independent geologist signoff queue · ASX announcement draft template · 24h disclosure clock.",
    honest: "This is the layer ASX-listed juniors care about most. The risk-weighting is conservative by design — false positives cost a delayed announcement, false negatives end careers. I'd rather over-flag and let your CS team decide than under-flag and find out at the AGM.",
  },
  {
    Icon: Compass,
    code: "05",
    name: "Capital-Allocation Brief",
    plain: "Where should the next A$2M of drilling spend go? DARA synthesises the geology, the budget, the seasonal access windows, the JV obligations, the work-programme commitments, and produces a ranked list of next-five-drill-holes by expected EV impact. The board sees the math behind each recommendation.",
    builtIn: "Ranked drill-hole queue · EV-per-metre model · wet-season access cutoff awareness · expenditure-commitment tracking · joint-venture obligation calendar.",
    honest: "The chief geologist will probably override DARA's ranking on at least one hole per cycle. That's fine. The point isn't to replace their judgement — it's to give the board a defensible reason for the capital allocation that doesn't depend on the geologist's memory.",
  },
  {
    Icon: Eye,
    code: "06",
    name: "Competitor & Government-Data Watch",
    plain: "Every ASX/TSX-V JORC announcement in your belt. Every GSWA / Geoscience Australia / MRIWA open-file release. Every state-government rezoning notice. Continuously read, continuously summarised, continuously linked back to your tenement geography. 'Competitor X just announced 28m at 3.2 g/t 2km from your eastern boundary — here's the side-by-side.'",
    builtIn: "Continuous monitoring of public regulatory feeds and competitor disclosures across your jurisdiction · proximity-weighted relevance · tenement-boundary geo-fence · daily competitor digest.",
    honest: "Most exploration teams find out about a neighbour's intercept from a phone call from a broker. This layer means you find out from DARA at 6am, with the geological context already attached, before the market opens.",
  },
];

export default function Dayo() {
  useNoIndex();
  usePageview();

  const [form, setForm] = useState({
    email: "",
    name: "Dayo",
    role: "Financial broker · resources sector",
    focus: "Introduce 1-2 of my exploration clients to DARA for a paid POC",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Work email is required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/industry-leads`, {
        email: form.email,
        name: form.name,
        company: "Channel partner · resources sector",
        role: form.role,
        message: [
          form.focus ? `Focus: ${form.focus}` : "",
          form.message ? `Notes: ${form.message}` : "",
        ].filter(Boolean).join("\n"),
        industry: "dayo",
        source: "dayo-bespoke",
        visitor_session: getVisitorSession(),
      });
      setSubmitted(true);
      toast.success("Received — Manuel will reach out within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="dayo-page">
      <BespokePageRibbon prospectName="Dayo · Resources Channel" />
      <BespokeWatermark pageLabel="DAYO" position="top" />

      {/* Top nav */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="dayo-back-to-home">
            <span className="text-[#FFB000] font-mono text-sm">{`>_`}</span>
            <span className="font-heading font-black tracking-tight text-white text-base">DARA</span>
            <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 ml-2">
              · A NOTE FOR DAYO
            </span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <a href="#engage" data-testid="dayo-nav-engage" className="bg-[#FFB000] text-black font-bold font-mono text-[11px] sm:text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90">
              Reply to Manuel
            </a>
          </div>
        </div>
      </nav>

      {/* HERO — direct response to Dayo's objection */}
      <section className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#10283F]/40 via-transparent to-[#FFB000]/5 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 relative">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-4">
            // A NOTE FOR DAYO · FROM MANUEL · JUNE 2026
          </div>
          <div className="flex items-center gap-3 mb-4">
            <Mountain className="w-6 h-6 text-[#FFB000]" />
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-400">
              FOR DAYO · RESOURCES SECTOR CHANNEL
            </div>
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            You asked the right question. <span className="text-[#FFB000]">Here's how DARA works in exploration — where there's no SCADA to plug into.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            When we met, you watched the Ventia industrial-connector demo and asked: <i>"how does DARA work in exploration, where there are no sensors, no live data streams, just drill holes and depth?"</i> It's the sharpest question I've had about the product, and the honest answer is that the Ventia demo is the operational case. Exploration is a different data shape and a different value prop. I built this page so you can see it.
          </p>
          <p className="mt-4 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            DARA in exploration is the same shape as DARA in operations: <b>the daily synthesis layer above the tools the team already uses</b>. We don't replace Leapfrog, Datamine, Vulcan, Micromine, or any of the 3D modelling suites your clients run. We read from them. We synthesise. We produce the brief that nobody on a small geological team has time to assemble manually on a Monday morning.
          </p>
          <p className="mt-4 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            Below: the six value-add layers DARA produces in exploration. A downloadable mock of the actual daily brief — with a synthesised anomaly heatmap as the hero — sits at the bottom of this page so you can hand it to a client. And the strategic-partner commercial structure is laid out plainly — handshake first, paper on the back end.
          </p>

          <div className="mt-10 flex items-center gap-4 flex-wrap">
            <a
              href="#engage"
              data-testid="dayo-hero-engage-cta"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90"
            >
              <ChevronRight className="w-4 h-4" /> Reply to Manuel <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#brief"
              data-testid="dayo-hero-brief-link"
              className="font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-[#FFB000] underline-offset-4 hover:underline flex items-center gap-1.5"
            >
              <FileText className="w-3.5 h-3.5" /> Download the mock heatmap brief
            </a>
          </div>
        </div>
      </section>

      {/* THE FOUR HONEST GAPS — what DARA isn't */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // FIRST · WHAT DARA IS NOT IN EXPLORATION
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            Four things I want to clear up before we talk about what DARA <span className="text-[#FFB000]">does</span> do.
          </h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-3xl leading-relaxed">
            Every vendor in this space oversells. I'd rather your clients hear what DARA isn't before they hear what it is. Saves us both time.
          </p>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900" data-testid="dayo-not-grid">
            <GapBlock
              code="01"
              title="Not the 3D geological modelling tool"
              body="Leapfrog, Datamine, Vulcan, Surpac, Micromine own that surface and they own it well. DARA reads from them, doesn't replace them. Your client's principal geologist keeps using whatever modelling suite they already trust."
            />
            <GapBlock
              code="02"
              title="Not the lab"
              body="The assay quality control still belongs to ALS, SGS, Bureau Veritas, Intertek. We synthesise their outputs, we don't validate them. Chain-of-custody and QA/QC stay with the lab."
            />
            <GapBlock
              code="03"
              title="Not the field-data capture tool"
              body="Strayos, ImaGo, RockMass, GeoMate, the in-house core-shed apps your clients already run. DARA ingests from them. We're not asking your client's geologists to learn a new field-capture interface."
            />
            <GapBlock
              code="04"
              title="Not a magic discovery machine"
              body="The chief geologist still makes the call on every hole. DARA's job is to make sure they're making it with the full picture in front of them every day, without manually stitching six data sources. We're not pretending to discover gold."
            />
          </div>
        </div>
      </section>

      {/* THE SIX VALUE-ADDS */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // SIX VALUE-ADDS · WHAT DARA DOES PRODUCE
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            Six daily artefacts. <span className="text-[#FFB000]">All synthesised from data your client already collects.</span>
          </h2>
          <p className="mt-3 text-sm text-zinc-400 max-w-3xl leading-relaxed">
            Each layer is built to be reviewed in under a minute by the geologist or director it's addressed to. No new tools. No new data collection. Just the synthesis layer above what's already happening.
          </p>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900" data-testid="dayo-layers-grid">
            {EXPLORATION_LAYERS.map((layer) => (
              <LayerBlock key={layer.code} {...layer} />
            ))}
          </div>
        </div>
      </section>

      {/* QUICK ANSWERS — customer-facing only · NO pipeline disclosure */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // QUICK ANSWERS · FOR YOUR CHIEF GEO + CFO
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            Three questions your team will ask. <span className="text-[#FFB000]">Deeper technical questions, ask me directly.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-3xl leading-relaxed">
            The full technical pipeline isn't on this page on purpose. If your principal geologist wants to walk through it, we do that over a call where I can answer the specific question and you can read my body language at the same time.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900" data-testid="dayo-tech-faq-grid">

            <FAQBlock
              code="Q1"
              question="What does 'VRC' actually mean?"
              answer={
                <>
                  <p className="text-sm text-zinc-300 leading-relaxed mb-2">
                    <b>VRC = Vertical Reverse Circulation</b> drill hole. Standard AU exploration drill-prefix convention. Other prefixes you'll see: <b>RC</b> (angled or vertical Reverse Circulation, chips), <b>DD/DDH</b> (Diamond Drill Hole, continuous core), <b>AC</b> (Air Core, shallow geochem), <b>RAB</b> (Rotary Air Blast, grid sampling).
                  </p>
                  <p className="text-sm text-zinc-300 leading-relaxed">
                    The mock brief uses VRC024 because the fictional Aurora project is testing a horizontal mineralised zone where vertical holes give cleanest depth intercepts. In a real DARA deployment, we don't impose codes — <b>we inherit whatever convention your geology team already uses</b> in their drill database.
                  </p>
                </>
              }
            />

            <FAQBlock
              code="Q2"
              question="Does DARA replace Leapfrog / Datamine / Vulcan?"
              answer={
                <>
                  <p className="text-sm text-zinc-300 leading-relaxed mb-2">
                    <b>No.</b> Those tools model the 3D orebody and they own that surface. DARA sits above them. We synthesise everything that touches the tenement into one daily picture, then your modelling tools keep doing what they do well.
                  </p>
                  <p className="text-sm text-zinc-300 leading-relaxed">
                    The chief geologist's interpretation, the lab QA/QC, and your field data capture all stay where they are. DARA is the layer above the layer.
                  </p>
                </>
              }
            />

            <FAQBlock
              code="Q3"
              question="How long does onboarding take?"
              answer={
                <>
                  <p className="text-sm text-zinc-300 leading-relaxed mb-2">
                    <b>15 working days to first stable daily brief — workspace-first.</b> Day 1: client provisions their DARA workspace (per-tenant, AES-256-keyed, audit-logged). Empty by design.
                  </p>
                  <p className="text-sm text-zinc-300 leading-relaxed mb-2">
                    Day 1-14 (the trial): client uploads to <b>their own workspace</b> at their own pace. <b>Data never leaves their tenant.</b> First brief fires 24 hours after first upload. Day 11-14: calibration call.
                  </p>
                  <p className="text-sm text-zinc-300 leading-relaxed">
                    Day 15+: daily brief lands at 06:00 local time. If they decline to continue, workspace goes to 60-day cold-storage with self-serve export. Onboarding cost A$15-25k folded into the POC fee.
                  </p>
                </>
              }
            />

          </div>
        </div>
      </section>

      {/* PRICING LADDER · 14-day trial + 3 paid tiers */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // PRICING · EXPLORATION TIERS · ANCHORED ON WHAT THE ALTERNATIVE COSTS YOUR CLIENT
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            Fourteen days free. Then three tiers, all priced <span className="text-[#FFB000]">at less than the alternative your client is already running.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-3xl leading-relaxed">
            Your clients spend A$5-8k a day on a drill rig and A$3-15k a batch on assay results. They don't blink at that. The mental anchor for them is <b>"is this less than one extra geologist on the team?"</b> — and yes, for every tier below, the answer is yes.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900" data-testid="dayo-pricing-grid">

            <PricingTier
              tag="LIGHTHOUSE PILOT"
              price="A$0"
              cadence="14 days · free"
              testid="dayo-tier-lighthouse"
              features={[
                "1 project area",
                "Up to 5 user seats",
                "Full feature set",
                "Workspace persists 60 days post-trial",
                "Self-serve export · no lock-in",
              ]}
              fitLine="Designed to prove the morning brief is useful · no commitment required"
              accent="zinc"
            />

            <PricingTier
              tag="TIER 1 · JUNIOR"
              price="Contact"
              cadence="for pricing"
              testid="dayo-tier-1"
              features={[
                "1 active project area",
                "Up to 5 user seats",
                "All 6 value-add layers · daily refresh",
                "All 11 jurisdictional rulebooks",
                "Next-business-day support SLA",
              ]}
              fitLine="Pre-revenue ASX-listed junior · A$5-50M cap · 1-3 geologists"
              accent="amber"
            />

            <PricingTier
              tag="TIER 2 · MULTI-PROJECT"
              price="Contact"
              cadence="for pricing"
              testid="dayo-tier-2"
              features={[
                "Up to 5 active project areas",
                "Up to 15 user seats",
                "Cross-project synthesis · intra-day refresh on material assays",
                "Quarterly weighting recalibration",
                "4 hrs / month dedicated CSM",
              ]}
              fitLine="Mid-tier ASX / TSX-V explorer · A$50-300M cap · 5-15 geologists"
              accent="amber"
            />

            <PricingTier
              tag="TIER 3 · ENTERPRISE"
              price="Contact"
              cadence="for pricing"
              testid="dayo-tier-3"
              features={[
                "Unlimited projects + seats",
                "Multi-jurisdiction (JORC · NI 43-101 · SK-1300 · PERC · SAMREC)",
                "Real-time refresh · direct integrations to your existing systems",
                "Dedicated account team",
                "Custom regulatory coverage on request",
              ]}
              fitLine="Major exploration division · BHP / Rio / Northern Star / Newmont scale"
              accent="navy"
            />

          </div>

          <div className="mt-10 max-w-5xl border border-zinc-800 bg-[#0a0a0a] p-6 sm:p-8" data-testid="dayo-pricing-roi">
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
              // THE MATH YOUR CLIENT WILL RUN IN THEIR HEAD
            </div>
            <p className="text-sm sm:text-base text-zinc-200 leading-relaxed mb-3">
              For comparison, here's what every line item actually costs your client today:
            </p>
            <ul className="space-y-2 text-sm text-zinc-300 leading-relaxed">
              <li><b className="text-[#FFB000]">One graduate geologist FTE (fully loaded)</b> — A$115,000 to A$160,000 per year. And they can't do most of what DARA produces because they can't read 11 rulebooks daily.</li>
              <li><b className="text-[#FFB000]">One RC drill rig running for a week</b> — A$30,000 to A$45,000. One hole, 100m deep.</li>
              <li><b className="text-[#FFB000]">One batch of assay results from ALS / SGS / Bureau Veritas</b> — A$3,000 to A$15,000.</li>
              <li><b className="text-[#FFB000]">Bloomberg Terminal (the actual metaphor)</b> — US$30,000 per seat per year. No trader questions it.</li>
              <li><b className="text-[#FFB000]">Sphera / IsoMetrix mining compliance</b> — A$80,000 to A$250,000 per site per year. Compliance posture only — no synthesis.</li>
            </ul>
            <p className="mt-4 text-sm sm:text-base text-zinc-200 leading-relaxed">
              <b>The math your client will run for themselves.</b> For a senior geologist FTE, your client pays A$200k+. For DARA they pay a tiny fraction of that — and unlike a geo, DARA covers 11 rulebooks daily. Contact me for the specific quote that fits your client's scale.
            </p>
          </div>

        </div>
      </section>

      {/* DOWNLOAD MOCK BRIEF */}
      <section id="brief" className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // THE MOCK BRIEF · TANGIBLE ARTEFACT YOU CAN SHARE
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            A sample of the actual morning brief — <span className="text-[#FFB000]">with a synthesised anomaly heatmap.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-3xl leading-relaxed">
            I built a mock daily brief for a fictional gold/copper explorer called Aurora Resources Ltd, working a tenement in the Eastern Goldfields WA. Real exploration data shape — drill assays, JORC materiality, competitor watch, capital allocation, native-title status. Hand this to any client who wants to know what DARA actually produces. It's the artefact, not the pitch deck.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 items-center">
            <a
              href="/api/public/sample-brief/exploration-morning-brief"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dayo-download-brief"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90"
            >
              <FileText className="w-4 h-4" /> Download the mock morning brief (PDF)
            </a>
            <span className="font-mono text-[11px] text-zinc-500">
              Single-page A4 · ~20KB · mock data · shareable
            </span>
          </div>

          {/* Phase 25.16ay — Self-serve trial CTA · Dayo asked for this directly */}
          <div className="mt-10 border border-[#FFB000]/40 bg-[#0f0b00] p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6 items-center" data-testid="dayo-trial-cta">
            <div className="lg:col-span-2">
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">
                // OR · TRY IT WITH YOUR OWN HOLES · LIVE
              </div>
              <h3 className="font-heading font-black text-xl sm:text-2xl tracking-tight mb-3">
                Plug your overnight assays into DARA. <span className="text-[#FFB000]">See the brief synthesise in 60 seconds.</span>
              </h3>
              <p className="text-sm text-zinc-300 leading-relaxed">
                We've pre-loaded a real WA tenement (Mt Magnet · Yilgarn greenstone) with 10 fabricated assays so you can run the brief immediately. Replace any row with your client's actual numbers — or load yours from scratch — and re-run. The DARA Engine ranks intercepts by grade × width, applies JORC 2012 materiality, generates a synthesised heatmap, and writes the brief in plain English. PDF download at the end.
              </p>
            </div>
            <Link
              to="/exploration-trial"
              data-testid="dayo-trial-cta-btn"
              className="inline-flex items-center gap-2 justify-center bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-4 hover:opacity-90 whitespace-nowrap"
            >
              Open the trial <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* COMMERCIAL STRUCTURE — strategic partner, handshake-first */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // STRATEGIC PARTNER STRUCTURE · HANDSHAKE FIRST · PAPER LATER
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            No commission cap. No contract upfront. <span className="text-[#FFB000]">Bring wins, we figure out fair on the back end.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-3xl leading-relaxed">
            You move real money for real people. A commission-cap structure in conversation 1 would be the wrong vocabulary for the relationship you're in. Senior commercial introductions don't work that way — handshake first, paper on the back end based on what actually got delivered. That's the model below.
          </p>
          <p className="mt-3 text-sm sm:text-base text-zinc-300 max-w-3xl leading-relaxed">
            The risk of doing it this way is on me — it depends on me actually being generous when the win arrives. <b>That's a feature, not a bug</b>: if I bait-and-switch, you'd tell every broker in WA and burn the channel for me. So the handshake model forces founder discipline. You can trust it because I can't afford for it to fail.
          </p>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900" data-testid="dayo-stages-grid">
            <StageBlock
              tag="STAGE 1"
              title="Open Partnership · no contract, no cap"
              trigger="From day one · bring an exploration client to DARA"
              terms="If we close, you're publicly named as the strategic partner who introduced them. Once that first paid POC closes, you and I sit down and agree what's fair — could be cash, could be equity, could be a board observer seat, could be a revenue share. Whichever fits the outcome and what YOU want from the relationship. Bring small wins, we figure out small fair. Bring a A$1M annual contract, we figure out big fair. Your upside scales with the deal, not with a bracket I picked before knowing what you delivered."
              testid="dayo-stage-1"
            />
            <StageBlock
              tag="STAGE 2"
              title="Formal Strategic Partner · papered up"
              trigger="Triggered at 1 closed POC OR 1 annual contract"
              terms="Whatever we agreed at the end of Stage 1 gets papered properly under WA jurisdiction. Advisor-level access — 30-min monthly call with me, named publicly across the DARA website + decks + investor materials. If at this stage you want equity, that's your call and we structure it (vesting, cliff, advisor agreement). If you want cash, also fine. If you want both, also fine. Your shape."
              testid="dayo-stage-2"
            />
            <StageBlock
              tag="STAGE 3"
              title="Named Founding Partner"
              trigger="Triggered at 3 closed POCs"
              terms="Recognition tier. Public listing as Founding Strategic Partner across every DARA surface — site, decks, investor materials, press. Material upside in whichever form we negotiated at Stage 2 (equity / cash / hybrid). The kind of relationship that gets named in the eventual IPO prospectus."
              testid="dayo-stage-3"
            />
          </div>

          <p className="mt-8 text-sm text-zinc-400 max-w-3xl leading-relaxed italic">
            All three stages are reversible at any point if it isn't working for either side. No contract in your way at Stage 1 — you can start introducing tomorrow without sending anything to a lawyer.
          </p>
        </div>
      </section>

      {/* THE ASK */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // THE ASK · WHAT I'D LIKE FROM YOU
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            Three names. <span className="text-[#FFB000]">Specific, not theoretical.</span>
          </h2>
          <p className="mt-6 text-base text-zinc-300 max-w-3xl leading-relaxed">
            When you're ready to take the conversation forward, the most useful thing you can do is name three specific exploration clients you think would benefit from DARA. Company names, the principal geologist or CFO you'd introduce me to, and the last conversation you had with them. With that, I can prepare a tailored mock brief for one of them and we approach together as strategic partners — no contract in your way, no commission cap, just bring the win and we figure out fair on the back end.
          </p>
          <p className="mt-4 text-base text-zinc-300 max-w-3xl leading-relaxed">
            If you'd rather sit on the conversation for a quarter and circle back — also fine. The mock brief above is yours to keep regardless. No timeline pressure on this from my end.
          </p>
        </div>
      </section>

      {/* ENGAGE */}
      <section id="engage" className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
                // REPLY TO MANUEL · NOTHING SENT TO ANYONE ELSE
              </div>
              <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight leading-tight">
                Drop a note. <span className="text-[#FFB000]">I'll write back within one business day.</span>
              </h2>
              <p className="mt-4 text-sm sm:text-base text-zinc-300 leading-relaxed">
                This form goes only to me. Not a sales team. Not a CRM dump. Just the two of us continuing the conversation we started in person.
              </p>
              <p className="mt-4 text-xs sm:text-[11px] text-zinc-500 leading-relaxed italic">
                If you'd rather skip the form, you have my number from the meeting. Either works.
              </p>
            </div>

            {submitted ? (
              <div className="border border-[#FFB000]/40 bg-[#FFB000]/5 p-8" data-testid="dayo-engage-success">
                <CheckCircle2 className="w-10 h-10 text-[#FFB000] mb-4" />
                <h3 className="font-heading font-bold text-xl tracking-tight mb-2">Received.</h3>
                <p className="text-sm text-zinc-300 leading-relaxed">
                  Thanks Dayo. I'll write back within one business day. If anything urgent comes up before then, my mobile is in your inbox already.
                </p>
                <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                  // Manuel · DARA OS
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="dayo-engage-form">
                <Field label="Work email" required>
                  <input
                    type="email"
                    required
                    data-testid="dayo-form-email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                    placeholder="you@firm.com.au"
                  />
                </Field>
                <Field label="Your name">
                  <input
                    type="text"
                    data-testid="dayo-form-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                  />
                </Field>
                <Field label="Role / firm">
                  <input
                    type="text"
                    data-testid="dayo-form-role"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                  />
                </Field>
                <Field label="What you'd like to discuss">
                  <input
                    type="text"
                    data-testid="dayo-form-focus"
                    value={form.focus}
                    onChange={(e) => setForm({ ...form, focus: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                  />
                </Field>
                <Field label="Anything else" sub="Optional — names of clients you'd introduce, questions, push-back">
                  <textarea
                    rows={4}
                    data-testid="dayo-form-message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                  />
                </Field>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="dayo-form-submit"
                  className="w-full bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90 disabled:opacity-50"
                >
                  {submitting ? "Sending…" : "Reply to Manuel →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <BespokeWatermark pageLabel="DAYO" position="bottom" />

      {/* Footer */}
      <footer className="bg-black border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <p className="text-xs sm:text-[11px] text-zinc-500 leading-relaxed font-mono">
            DARA OS · Manuel Maji · majisonmanuel@gmail.com · +61 481 172 655 · Perth WA
          </p>
          <p className="mt-2 text-xs sm:text-[11px] text-zinc-600 leading-relaxed italic">
            Bespoke page · prepared for Dayo · not indexed · not linked from public surfaces · every visit is logged and traceable to the session that received this URL.
          </p>
        </div>
      </footer>
    </div>
  );
}

// ─── helpers ─────────────────────────────────────────────────

function PricingTier({ tag, price, cadence, features, fitLine, testid, accent }) {
  const accentClasses = {
    amber: "border-t-[#FFB000]",
    navy: "border-t-[#10283F]",
    zinc: "border-t-zinc-700",
  };
  const labelColor = {
    amber: "text-[#FFB000]",
    navy: "text-zinc-200",
    zinc: "text-zinc-400",
  };
  return (
    <div
      className={`bg-black p-6 sm:p-7 flex flex-col border-t-4 ${accentClasses[accent] || accentClasses.zinc}`}
      data-testid={testid}
    >
      <div className={`font-mono text-[11px] sm:text-[10px] uppercase tracking-widest mb-3 ${labelColor[accent] || labelColor.zinc}`}>
        // {tag}
      </div>
      <div className="mb-1">
        <span className="font-heading font-black text-2xl sm:text-3xl tracking-tight">{price}</span>
      </div>
      <div className="text-xs sm:text-[11px] text-zinc-500 font-mono mb-5">{cadence}</div>
      <ul className="space-y-2 text-xs sm:text-[13px] text-zinc-300 leading-relaxed mb-5 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="text-[#FFB000] mt-0.5">▸</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="border-t border-zinc-900 pt-3 mt-auto">
        <p className="text-[11px] text-zinc-500 italic leading-relaxed">{fitLine}</p>
      </div>
    </div>
  );
}

function FAQBlock({ code, question, answer }) {
  return (
    <div className="bg-black p-6 sm:p-8 flex flex-col" data-testid={`dayo-faq-${code.toLowerCase()}`}>
      <div className="flex items-center gap-3 mb-3">
        <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000]">
          // {code}
        </span>
      </div>
      <h3 className="font-heading font-bold text-lg sm:text-xl tracking-tight mb-4 leading-snug">{question}</h3>
      <div className="space-y-2">{answer}</div>
    </div>
  );
}

function GapBlock({ code, title, body }) {
  return (
    <div className="bg-black p-6 sm:p-8 flex flex-col" data-testid={`dayo-not-${code}`}>
      <div className="flex items-center gap-3 mb-3">
        <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#A1331F]">
          // NOT THIS · {code}
        </span>
      </div>
      <h3 className="font-heading font-bold text-lg tracking-tight mb-3">{title}</h3>
      <p className="text-sm text-zinc-300 leading-relaxed">{body}</p>
    </div>
  );
}

function LayerBlock({ Icon, code, name, plain, builtIn, honest }) {
  return (
    <div className="bg-black p-6 sm:p-8 flex flex-col" data-testid={`dayo-layer-${code}`}>
      <div className="flex items-center gap-3 mb-3">
        <Icon className="w-5 h-5 text-[#FFB000]" />
        <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000]">
          // {code}
        </span>
      </div>
      <h3 className="font-heading font-bold text-xl tracking-tight mb-3" dangerouslySetInnerHTML={{ __html: name }} />
      <p className="text-sm text-zinc-300 leading-relaxed mb-4">{plain}</p>
      <div className="border-t border-zinc-900 pt-4 space-y-2.5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-1">// BUILT-IN</div>
          <p className="text-xs sm:text-[11px] text-zinc-400 leading-relaxed">{builtIn}</p>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">// HONEST READ</div>
          <p className="text-xs sm:text-[11px] text-zinc-400 italic leading-relaxed">{honest}</p>
        </div>
      </div>
    </div>
  );
}

function StageBlock({ tag, title, trigger, terms, testid }) {
  return (
    <div className="bg-black p-6 sm:p-8 flex flex-col" data-testid={testid}>
      <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
        // {tag}
      </div>
      <h3 className="font-heading font-bold text-xl tracking-tight mb-3">{title}</h3>
      <div className="border-t border-zinc-900 pt-3 mb-3">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">// TRIGGERED WHEN</div>
        <p className="text-sm text-zinc-300 leading-relaxed">{trigger}</p>
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">// TERMS</div>
        <p className="text-sm text-zinc-300 leading-relaxed">{terms}</p>
      </div>
    </div>
  );
}

function Field({ label, sub, required, children }) {
  return (
    <div>
      <label className="block font-mono text-[11px] uppercase tracking-widest text-zinc-400 mb-1.5">
        {label}{required && <span className="text-[#FFB000] ml-1">*</span>}
        {sub && <span className="text-zinc-600 ml-2 normal-case tracking-normal italic">{sub}</span>}
      </label>
      {children}
    </div>
  );
}
